import { useAuth } from '../context/AuthContext';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [resetUrl, setResetUrl] = useState<string | null>(null);

  const { forgotPassword } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessMessage(null);
    setResetUrl(null);

    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsSubmitting(true);
    try {
      const res: any = await forgotPassword({ email });

      const msg =
        res?.message || 'If an account exists for that email, we sent password reset instructions.';
      setSuccessMessage(msg);
      toast.success(msg);

      if (res?.url && typeof res.url === 'string') {
        setResetUrl(res.url);
      } else if (res?.data?.url && typeof res.data.url === 'string') {
        setResetUrl(res.data.url);
      }
    } catch (err: any) {
      const msg =
        err?.message || 'Failed to request password reset. Please try again later.';
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-agentic-white-off">
      <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
        {/* Left Panel - same visuals as Login */}
        <div className="hidden lg:flex relative p-8">
          <div className="relative w-full rounded-3xl overflow-hidden bg-gradient-to-br from-blue-200 via-blue-300 to-blue-400">
            <div className="absolute inset-0 opacity-70">
              <div className="absolute -top-10 -left-10 w-80 h-80 bg-white/30 rounded-full blur-3xl"></div>
              <div className="absolute bottom-10 right-10 w-96 h-96 bg-white/20 rounded-full blur-3xl"></div>
            </div>

            <div className="relative p-10 space-y-8">
              <div className="max-w-xl">
                <h2 className="text-3xl font-semibold text-gray-800 leading-tight">
                  Orchestrate your Agentic AI workforce
                </h2>
                <p className="mt-3 text-gray-700">
                  Coordinate autonomous agents across claims, underwriting, fraud, and support — securely, at enterprise scale.
                </p>
              </div>

              <div className="space-y-6 max-w-md">
                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md">
                  <div className="text-xs font-semibold text-gray-500 tracking-wide">AGENTS ONLINE</div>
                  <div className="mt-2 flex items-end space-x-2">
                    <div className="text-4xl font-bold text-gray-900">18</div>
                    <div className="text-xs text-gray-500 mb-2">live</div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Health across OCR, Customer Support, Compliance, Fraud, and Reporting agents
                  </p>
                  <div className="mt-4 flex items-end space-x-2 h-24">
                    <div className="w-6 bg-green-200 h-6 rounded"></div>
                    <div className="w-6 bg-green-300 h-10 rounded"></div>
                    <div className="w-6 bg-green-400 h-12 rounded"></div>
                    <div className="w-6 bg-green-500 h-20 rounded"></div>
                  </div>
                </div>

                <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-6 shadow-md">
                  <div className="text-xs font-semibold text-gray-500 tracking-wide">AUTOMATIONS EXECUTED</div>
                  <div className="mt-2 flex items-end space-x-2">
                    <div className="text-4xl font-bold text-gray-900">26.8K</div>
                    <div className="text-xs text-gray-500 mb-2">last month</div>
                  </div>
                  <p className="mt-2 text-sm text-gray-600">
                    Automated triage, enrichment, and decisioning across enterprise workflows
                  </p>
                  <div className="mt-4 flex items-end space-x-2 h-24">
                    <div className="w-6 bg-blue-200 h-10 rounded"></div>
                    <div className="w-6 bg-blue-300 h-16 rounded"></div>
                    <div className="w-6 bg-blue-400 h-20 rounded"></div>
                    <div className="w-6 bg-blue-500 h-24 rounded"></div>
                  </div>
                </div>
              </div>

              <div className="max-w-md mt-6">
                <div className="bg-white/70 rounded-2xl p-6 shadow-inner">
                  <svg className="w-full h-40" viewBox="0 0 400 160">
                    <defs>
                      <linearGradient id="node" x1="0" x2="1">
                        <stop offset="0%" stopColor="#93c5fd" />
                        <stop offset="100%" stopColor="#3b82f6" />
                      </linearGradient>
                    </defs>
                    <g stroke="#93c5fd" strokeWidth="2" opacity="0.8">
                      <line x1="60" y1="80" x2="160" y2="40" />
                      <line x1="60" y1="80" x2="160" y2="120" />
                      <line x1="160" y1="40" x2="300" y2="80" />
                      <line x1="160" y1="120" x2="300" y2="80" />
                    </g>
                    <g>
                      <circle cx="60" cy="80" r="12" fill="url(#node)" />
                      <rect x="152" y="32" width="16" height="16" rx="3" fill="url(#node)" />
                      <rect x="152" y="112" width="16" height="16" rx="3" fill="url(#node)" />
                      <circle cx="300" cy="80" r="12" fill="url(#node)" />
                    </g>
                  </svg>
                  <div className="text-xs text-gray-600 text-center">Autonomous agents collaborating across your enterprise graph</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Forgot Password Form */}
        <div className="flex items-center justify-center p-6 lg:p-12 bg-white">
          <div className="w-full max-w-md">
            <div className="flex items-center mb-6">
              <img src={process.env.REACT_APP_APPLICATION_LOGO_URL || "agentic-logo.png"} alt={process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform'} className="h-16 w-auto" loading="eager" referrerPolicy="no-referrer" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-1">Forgot password</h1>
            <p className="text-gray-600 mb-8">Enter your email and we'll send instructions to reset your password.</p>

            <form onSubmit={handleSubmit} className="bg-white rounded-2xl p-6 shadow-sm space-y-4">
              <label className="flex flex-col text-sm">
                <span className="text-gray-600 text-xs font-medium">Email</span>
                <input
                  required
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  className="mt-1 p-3 rounded-lg border border-gray-200 focus:ring-2 focus:ring-blue-200"
                  placeholder="you@company.com"
                />
              </label>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 rounded-lg bg-agentic-primary-blue text-white font-semibold disabled:opacity-60"
                >
                  {isSubmitting ? 'Sending...' : 'Send reset instructions'}
                </button>
              </div>

              {/* Success / Error UI */}
              {successMessage && (
                <div className="mt-2 text-sm text-green-700 bg-green-50 p-3 rounded">
                  <div>{successMessage}</div>

                  {/* Show "View email" button when resetUrl is available */}
                  {resetUrl && (
                    <div className="mt-3 flex justify-center">
                      <a
                        href={resetUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-block px-4 py-2 rounded-lg border border-transparent bg-white text-agentic-primary-blue font-medium shadow-sm hover:bg-gray-50"
                      >
                        View email
                      </a>
                    </div>
                  )}
                </div>
              )}

              {error && <div className="mt-2 text-sm text-red-700 bg-red-50 p-3 rounded">{error}</div>}

              <div className="text-center text-sm text-gray-500">
                Remembered your password?{' '}
                <button onClick={() => navigate('/login')} type="button" className="text-agentic-primary-blue hover:text-agentic-primary-blue-dark font-medium">
                  Sign in
                </button>
              </div>
            </form>

            <div className="mt-6 text-center text-xs text-gray-400">© 2026 {process.env.REACT_APP_APPLICATION_NAME || 'Agentic Platform'}, Inc. All rights reserved. END-TO-END CONVERGENCE</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
